/**
 * Wishlists — what dealers have saved, from both directions:
 *   • By product: which dealers want this model (the follow-up list)
 *   • By dealer:  everything one dealer has saved
 *
 * COST: the saved lists live on the user documents this page already streams, so
 * the rollup itself costs no extra reads. Only the products that someone has
 * actually saved are fetched for their model numbers (see useProductsByIds), so
 * cost scales with saves — tens — never with catalogue size.
 */
import { useMemo, useState } from 'react';
import type { Product, User } from '@mitang/shared';
import { useUsers } from '../hooks/useFirestore';
import { useProductsByIds } from '../hooks/useProductsByIds';
import { dealersWithWishlists, wishlistedProducts } from '../lib/analytics';
import { toCsv, downloadCsv } from '../lib/csv';
import { formatDateTime, timeAgo } from '../lib/format';
import { Badge, Button, Card, SectionHeader } from '../components/ui';
import { EmptyState, ErrorState, LoadingState } from '../components/States';

/** "1 save" / "2 saves" — small thing, but the header reads badly without it. */
function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

type View = 'product' | 'dealer';

/** Model number if we could resolve it, else the raw id so nothing is hidden. */
function label(productId: string, products: Map<string, Product>): string {
  return products.get(productId)?.modelNumber ?? productId;
}

function dealerName(user: User): string {
  return user.name || user.email || user.id;
}

export function WishlistsPage(): JSX.Element {
  const users = useUsers();
  const [view, setView] = useState<View>('product');
  const [openId, setOpenId] = useState<string | null>(null);

  const byProduct = useMemo(() => wishlistedProducts(users.data), [users.data]);
  const byDealer = useMemo(() => dealersWithWishlists(users.data), [users.data]);

  const savedIds = useMemo(() => byProduct.map((r) => r.productId), [byProduct]);
  const products = useProductsByIds(savedIds);

  const totalSaves = byProduct.reduce((n, r) => n + r.savers.length, 0);

  const onExport = (): void => {
    const rows = byProduct.flatMap((row) =>
      row.savers.map((s) => ({
        modelNumber: label(row.productId, products),
        category: products.get(row.productId)?.category ?? '',
        productId: row.productId,
        dealer: dealerName(s.user),
        company: s.user.companyName ?? '',
        mobile: s.user.mobile ?? '',
        email: s.user.email ?? '',
        savedAt: s.at ? formatDateTime(s.at) : '',
      })),
    );
    downloadCsv(
      'wishlists.csv',
      toCsv(rows, [
        { key: 'modelNumber', header: 'Model no' },
        { key: 'category', header: 'Category' },
        { key: 'productId', header: 'Product ID' },
        { key: 'dealer', header: 'Dealer' },
        { key: 'company', header: 'Company' },
        { key: 'mobile', header: 'Mobile' },
        { key: 'email', header: 'Email' },
        { key: 'savedAt', header: 'Saved at' },
      ]),
    );
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Wishlists"
        subtitle={`${plural(totalSaves, 'save')} across ${plural(byProduct.length, 'product')} by ${plural(byDealer.length, 'dealer')}. A save is a dealer telling you what they intend to buy.`}
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg bg-slate-100 p-1">
          {(
            [
              ['product', 'By product'],
              ['dealer', 'By dealer'],
            ] as Array<[View, string]>
          ).map(([key, text]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                view === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              {text}
            </button>
          ))}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={onExport}
          disabled={totalSaves === 0}
        >
          Export CSV
        </Button>
      </div>

      {users.error ? (
        <ErrorState message={users.error} />
      ) : users.loading ? (
        <LoadingState label="Loading wishlists…" />
      ) : totalSaves === 0 ? (
        <Card>
          <EmptyState
            title="No saved products yet"
            hint="Dealers save products with the heart in the app. Saves appear here immediately."
          />
        </Card>
      ) : view === 'product' ? (
        <Card>
          <ul className="divide-y divide-slate-100">
            {byProduct.map((row) => {
              const open = openId === row.productId;
              const product = products.get(row.productId);
              return (
                <li key={row.productId}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
                    onClick={() => setOpenId(open ? null : row.productId)}
                  >
                    <span className="w-4 shrink-0 text-slate-400">{open ? '▾' : '▸'}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800">
                        {label(row.productId, products)}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {[product?.category, product?.partNo].filter(Boolean).join(' · ') ||
                          'Not in the current price list'}
                      </span>
                    </span>
                    <Badge tone="warn">{plural(row.savers.length, 'save')}</Badge>
                    {row.lastSavedAt ? (
                      <span className="hidden shrink-0 text-xs text-slate-400 sm:block">
                        {timeAgo(row.lastSavedAt)}
                      </span>
                    ) : null}
                  </button>

                  {open ? (
                    <ul className="bg-slate-50/70 pb-2">
                      {row.savers.map(({ user, at }) => (
                        <li
                          key={user.id}
                          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-11 py-2 text-xs"
                        >
                          <span className="font-medium text-slate-800">{dealerName(user)}</span>
                          {user.companyName ? (
                            <span className="text-slate-500">{user.companyName}</span>
                          ) : null}
                          {user.mobile ? (
                            <>
                              <a href={`tel:${user.mobile}`} className="font-mono text-brand-600 hover:underline">
                                {user.mobile}
                              </a>
                              <a
                                href={`https://wa.me/${user.mobile.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
                                  `Hello, about ${label(row.productId, products)} —`,
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                                className="font-semibold text-emerald-600 hover:underline"
                              >
                                WhatsApp
                              </a>
                            </>
                          ) : null}
                          <span className="ml-auto text-slate-400">
                            {at ? `saved ${timeAgo(at)}` : 'saved earlier'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Card>
      ) : (
        <Card>
          <ul className="divide-y divide-slate-100">
            {byDealer.map((user) => {
              const open = openId === user.id;
              const ids = user.wishlist ?? [];
              return (
                <li key={user.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
                    onClick={() => setOpenId(open ? null : user.id)}
                  >
                    <span className="w-4 shrink-0 text-slate-400">{open ? '▾' : '▸'}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800">
                        {dealerName(user)}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {[user.companyName, user.city].filter(Boolean).join(' · ') ||
                          'No profile yet'}
                      </span>
                    </span>
                    <Badge tone="warn">{plural(ids.length, 'save')}</Badge>
                    {user.mobile ? (
                      <a
                        href={`tel:${user.mobile}`}
                        onClick={(e) => e.stopPropagation()}
                        className="hidden shrink-0 font-mono text-xs text-brand-600 hover:underline sm:block"
                      >
                        {user.mobile}
                      </a>
                    ) : null}
                  </button>

                  {open ? (
                    <ul className="bg-slate-50/70 pb-2">
                      {ids.map((id) => (
                        <li
                          key={id}
                          className="flex flex-wrap items-baseline gap-x-3 px-11 py-2 text-xs"
                        >
                          <span className="font-medium text-slate-800">
                            {label(id, products)}
                          </span>
                          <span className="text-slate-500">
                            {products.get(id)?.category ?? ''}
                          </span>
                          <span className="ml-auto text-slate-400">
                            {user.wishlistAt?.[id] ? `saved ${timeAgo(user.wishlistAt[id])}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
